import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';
import { getVehicleInfo, VehicleInfo } from '../../services/vehicle.service';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import GasMeterIcon from '@mui/icons-material/GasMeter';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';

const formatDate = (dateString: string): string => {
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Separar la fecha en sus componentes
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  
  // Los meses en el array están 0-based, por lo que restamos 1 al mes
  const monthName = months[month - 1];
  
  // Formatear el día para que siempre tenga dos dígitos
  const formattedDay = String(day).padStart(2, '0');

  return `${monthName} ${formattedDay} del ${year}`;
};

const VehicleSearch: React.FC = () => {
  useSessionTimeout();
  const [placa, setPlaca] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo | null>(null);
  const [error, setError] = useState('');
  const [expandedCylinders, setExpandedCylinders] = useState<boolean>(true);
  const [expandedRegulators, setExpandedRegulators] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await getVehicleInfo(placa);
      if (response.result) {
        setVehicleInfo(response.result);
        setError('');
      }
    } catch (err) {
      setError('Error al buscar el vehículo');
      setVehicleInfo(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
          <DirectionsCarIcon sx={{ fontSize: { xs: 24, sm: 30 }, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Consulta de Vehículos
          </Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            alignItems: 'flex-start',
            flexWrap: 'nowrap'
          }}>
            <TextField
              sx={{
                width: '400px',
                flexShrink: 1,
                '& .MuiInputLabel-root': {
                  fontSize: '1.2rem'
                }
              }}
              label="Placa o VIN del vehículo"
              value={placa}
              onChange={(e) => setPlaca(e.target.value.toUpperCase())}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DirectionsCarIcon color="primary" />
                  </InputAdornment>
                ),
                style: {
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.2rem'
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !placa}
              sx={{
                height: '56px',
                minWidth: '120px',
                flexShrink: 0
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <SearchIcon sx={{ mr: 1 }} />
                  Buscar
                </>
              )}
            </Button>
          </Box>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          {vehicleInfo && (
            <Box sx={{ mt: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <DirectionsCarIcon sx={{ mr: 1 }} />
                    Información del Vehículo
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ 
                        p: 2, 
                        mb: 2, 
                        backgroundColor: 'primary.main', 
                        borderRadius: 1,
                        color: 'white'
                      }}>
                        <Typography variant="h5" sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          fontWeight: 'bold',
                          letterSpacing: '0.2rem'
                        }}>
                          <DirectionsCarIcon sx={{ mr: 1, fontSize: 30 }} />
                          Placa: {vehicleInfo.PLACA}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <GasMeterIcon sx={{ mr: 1, fontSize: 20 }} />
                        <strong>VIN:</strong> {vehicleInfo.VIN}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <BuildIcon sx={{ mr: 1, fontSize: 20 }} />
                        <strong>Taller:</strong> {vehicleInfo.NOMBRE_TALLER_ULTIMA_CERTIFICACION}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon sx={{ mr: 1, fontSize: 20 }} />
                        <strong>Ciudad:</strong> {vehicleInfo.CIUDAD_UBICACION_TALLER}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <EventIcon sx={{ mr: 1, fontSize: 20 }} />
                        <strong>Fecha de Instalación:</strong> {vehicleInfo.FECHA_INSTALACION}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        mt: 2,
                        p: 3, 
                        backgroundColor: '#2c3e50',
                        borderRadius: 1,
                        textAlign: 'center',
                        boxShadow: 3
                      }}>
                        <Typography variant="h6" sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ecf0f1',
                          fontWeight: 'bold',
                          mb: 1
                        }}>
                          <EventIcon sx={{ mr: 1, fontSize: 28 }} />
                          Próxima Revisión
                        </Typography>
                        <Typography variant="h4" sx={{ 
                          fontWeight: 'bold',
                          color: '#e74c3c',
                          textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          {formatDate(vehicleInfo.FECHA_PROXIMA_REVISION_ANUAL)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    <Accordion 
                      expanded={expandedCylinders}
                      onChange={() => setExpandedCylinders(!expandedCylinders)}
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '& .MuiAccordionSummary-expandIconWrapper': {
                            color: 'white'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <GasMeterIcon sx={{ mr: 1 }} />
                          <Typography variant="h6">
                            Cilindros ({vehicleInfo.CILINDROS?.length || 0})
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {vehicleInfo.CILINDROS?.map((cilindro, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Card variant="outlined">
                                <CardContent sx={{ 
                                  '& .MuiTypography-root': { 
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    wordBreak: 'break-word'
                                  }
                                }}>
                                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                                    <GasMeterIcon sx={{ mr: 1, fontSize: 20 }} />
                                    <strong>Serial:</strong> {cilindro.NUMERO_SERIAL_CILINDRO}
                                  </Typography>
                                  <Typography><strong>Marca:</strong> {cilindro.MARCA_CILINDRO}</Typography>
                                  <Typography><strong>Capacidad:</strong> {cilindro.CAPACIDAD_CILINDRO}</Typography>
                                  <Typography><strong>Próxima PH:</strong> {cilindro.FECHA_PROXIMA_PH_CILINDRO}</Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion 
                      expanded={expandedRegulators}
                      onChange={() => setExpandedRegulators(!expandedRegulators)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '& .MuiAccordionSummary-expandIconWrapper': {
                            color: 'white'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SettingsIcon sx={{ mr: 1 }} />
                          <Typography variant="h6">
                            Reguladores ({vehicleInfo.REGULADORES?.length || 0})
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {vehicleInfo.REGULADORES?.map((regulador, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Card variant="outlined">
                                <CardContent sx={{ 
                                  '& .MuiTypography-root': { 
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    wordBreak: 'break-word'
                                  }
                                }}>
                                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
                                    <strong>Serial:</strong> {regulador.NUMERO_SERIAL_REGULADOR}
                                  </Typography>
                                  <Typography><strong>Marca:</strong> {regulador.MARCA_REGULADOR}</Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default VehicleSearch;
